import { useEffect, useMemo, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { X, ClipboardCheck } from 'lucide-react'
import { useWS } from '../contexts/WSContext'
import { apiError } from '../lib/api'

// Adoção: o operador só SELECIONA o que já está cadastrado (Cliente › Mina ›
// Veículo/modelo); o único campo editável é o nome de exibição (o vmc-id permanece
// a identidade real do device). O backend valida a hierarquia, gera o asset pack e
// grava o form_data enriquecido, que chega ao VMC via ext_payload do bootstrap.
//
// O PIT NÃO SE ESCOLHE AQUI: o equipamento recebe o documento de pits da mina inteiro
// (com as fotos, no asset pack) e quem escolhe é o operador, nos Ajustes do turno, a
// cada turno. Fixar um pit na adoção era um palpite que envelhecia no primeiro
// remanejamento.
export default function AdoptModal({ pending, onClose }) {
  const { t } = useTranslation()
  const { clients, mines, models, adminError, reloadAdmin } = useWS()
  const [form, setForm] = useState({ name: pending.vmc_name, clientId: '', mineId: '', modelId: '', notes: '' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [show, setShow] = useState(false)

  useEffect(() => { setShow(true); reloadAdmin() /* listas frescas ao abrir */ }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    setShow(false)
    setTimeout(onClose, 250)
  }

  const clientMines = useMemo(() => mines.filter(m => String(m.client_id) === form.clientId), [mines, form.clientId])

  // pré-seleção em cascata: primeiro item disponível de cada nível
  useEffect(() => {
    if (!form.clientId && clients.length) setForm(f => ({ ...f, clientId: String(clients[0].id) }))
  }, [clients]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setForm(f => ({ ...f, mineId: clientMines.length ? String(clientMines[0].id) : '' }))
  }, [form.clientId]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!form.modelId && models.length) setForm(f => ({ ...f, modelId: String(models[0].id) }))
  }, [models]) // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const body = {
        name: form.name.trim(),
        client_id: Number(form.clientId),
        mine_id: Number(form.mineId),
        equipment_model_id: Number(form.modelId),
        notes: form.notes,
      }
      const res = await fetch(`/api/pending-devices/${pending.id}/adopt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw await apiError(res)
      reloadAdmin()
      close()
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const selectedModel = models.find(m => String(m.id) === form.modelId)
  const noClients = clients.length === 0
  const noMines = form.clientId !== '' && clientMines.length === 0
  const noModels = models.length === 0
  const incomplete = !form.clientId || !form.mineId || !form.modelId

  const selectCls = 'mt-1 w-full text-sm border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-brand/30'
  const warn = (children) => (
    <p className="mt-1 text-xs text-status-stop bg-status-stop/10 rounded-md px-3 py-2">{children}</p>
  )

  return (
    <div className="fixed inset-0 z-50">
      <div
        onClick={close}
        className={`absolute inset-0 bg-slate-900/40 transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${show ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={18} className="text-brand" />
            <div>
              <h2 className="font-semibold text-slate-800 dark:text-slate-100">{t('admin:adopt.title')}</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">{pending.vmc_name}</p>
            </div>
          </div>
          <button onClick={close} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* Cadastro que não carregou não é cadastro vazio: sem isto o modal pedia
              para cadastrar cliente, mina e veículo que já existem. */}
          {adminError && (
            <div className="flex items-start gap-2 rounded-md border border-status-down/30 bg-status-down/10 px-3 py-2 text-xs text-status-down">
              <span className="flex-1">{t('admin:adopt.registry_error', { error: adminError })}</span>
              <button type="button" onClick={reloadAdmin} className="underline font-medium shrink-0">{t('common:actions.retry')}</button>
            </div>
          )}
          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:adopt.display_name')}</span>
            <input
              required
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder={t('admin:adopt.display_name_ph')}
              className="mt-1 w-full text-sm border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
            <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">{t('admin:adopt.display_name_help', { id: pending.vmc_name })}</p>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('common:entities.client')}</span>
            {noClients ? warn(<Trans i18nKey="admin:adopt.no_clients" components={[<strong key="0" />]} />) : (
              <select required value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} className={selectCls}>
                <option value="" disabled>{t('admin:select.client')}</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('common:entities.mine')}</span>
            {noMines ? warn(<Trans i18nKey="admin:adopt.no_mines" components={[<strong key="0" />]} />) : (
              <>
                <select required value={form.mineId} onChange={e => setForm({ ...form, mineId: e.target.value })} disabled={!form.clientId} className={selectCls}>
                  <option value="" disabled>{t('admin:select.mine')}</option>
                  {clientMines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">{t('admin:adopt.mine_help')}</p>
              </>
            )}
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:adopt.vehicle')}</span>
            {noModels ? warn(<Trans i18nKey="admin:adopt.no_models" components={[<strong key="0" />]} />) : (
              <>
                <select required value={form.modelId} onChange={e => setForm({ ...form, modelId: e.target.value })} className={selectCls}>
                  <option value="" disabled>{t('admin:select.vehicle')}</option>
                  {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                {selectedModel && (
                  <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                    {[selectedModel.category_name, selectedModel.manufacturer, selectedModel.checklist_name && t('admin:adopt.model_checklist', { name: selectedModel.checklist_name })].filter(Boolean).join(' · ')}
                  </p>
                )}
              </>
            )}
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:adopt.notes')}</span>
            <textarea
              rows={2}
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              className="mt-1 w-full text-sm border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </label>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </form>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button type="button" onClick={close} className="text-sm px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
            {t('common:actions.cancel')}
          </button>
          <button
            onClick={submit}
            disabled={submitting || incomplete}
            className="text-sm font-semibold px-3 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            {submitting ? t('admin:adopt.adopting') : t('admin:adopt.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
